<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, nextTick, ref, type ComputedRef, type Ref } from "vue";
import type { ProbeLatencyPoint } from "../api/measurements.js";
import type { DateTimeFormatPreference } from "../api/user-settings.js";
import { formatDateTime } from "../formatting/date-time.js";
import { clampTooltipPosition, horizontalTooltipBounds } from "./tooltip-position.js";

const props: Readonly<{ points: readonly ProbeLatencyPoint[]; dateTimeFormat: DateTimeFormatPreference }> = defineProps<{ points: readonly ProbeLatencyPoint[]; dateTimeFormat: DateTimeFormatPreference }>();
const chartWidth: number = 1000;
const chartHeight: number = 260;
const chartHorizontalPadding: number = 64;
const chartVerticalPadding: number = 28;
const tooltipGapPx: number = 14;
const tooltipViewportMarginPx: number = 12;
const tooltipElement: Ref<HTMLElement | null> = ref(null);
const tooltipPanel: Ref<{
  readonly visible: boolean;
  readonly text: string;
  readonly left: number;
  readonly top: number;
  readonly arrowLeft: number;
}> = ref({ visible: false, text: "", left: 0, top: 0, arrowLeft: 0 });

interface SeriesDefinition {
  readonly key: "primaryHttpLatencyMs" | "dnsLatencyMs" | "gatewayLatencyMs";
  readonly label: string;
  readonly color: string;
}
interface AxisLabel {
  readonly value: number;
  readonly label: string;
}
interface StatusSegment {
  readonly status: "degraded" | "interrupted" | "unknown";
  readonly firstIndex: number;
  readonly lastIndex: number;
}

const series: readonly SeriesDefinition[] = [
  { key: "primaryHttpLatencyMs", label: "Primary HTTP", color: "#37d7a0" },
  { key: "dnsLatencyMs", label: "DNS", color: "#76a9ff" },
  { key: "gatewayLatencyMs", label: "Gateway", color: "#f6ca7c" },
];

const maximumLatency: ComputedRef<number> = computed((): number => {
  const values: number[] = [];
  for (const point of props.points) {
    for (const definition of series) {
      const value: number | null = point[definition.key];
      if (value !== null) values.push(value);
    }
  }
  return Math.max(10, ...values) * 1.12;
});
const chartInnerWidth: number = chartWidth - chartHorizontalPadding * 2;
const chartInnerHeight: number = chartHeight - chartVerticalPadding * 2;
const yGridLines: readonly number[] = [0, 0.5, 1];
const yAxisLabels: ComputedRef<readonly AxisLabel[]> = computed((): readonly AxisLabel[] => [
  { value: maximumLatency.value, label: `${Math.ceil(maximumLatency.value)} ms` },
  { value: maximumLatency.value / 2, label: `${Math.round(maximumLatency.value / 2)} ms` },
  { value: 0, label: "0 ms" },
]);
const statusSegments: ComputedRef<readonly StatusSegment[]> = computed((): readonly StatusSegment[] => {
  const segments: StatusSegment[] = [];
  let segmentStatus: StatusSegment["status"] | null = null;
  let segmentFirstIndex: number = 0;
  for (let index: number = 0; index < props.points.length; index += 1) {
    const point: ProbeLatencyPoint | undefined = props.points[index];
    const status: ProbeLatencyPoint["status"] = point?.status ?? "unknown";
    const shadedStatus: StatusSegment["status"] | null = status === "connected" ? null : status;
    if (shadedStatus === segmentStatus) continue;
    if (segmentStatus !== null) {
      segments.push({ status: segmentStatus, firstIndex: segmentFirstIndex, lastIndex: index - 1 });
    }
    segmentStatus = shadedStatus;
    segmentFirstIndex = index;
  }
  if (segmentStatus !== null) {
    segments.push({ status: segmentStatus, firstIndex: segmentFirstIndex, lastIndex: props.points.length - 1 });
  }
  return segments;
});

function xFor(index: number): number {
  return props.points.length <= 1 ? chartHorizontalPadding + chartInnerWidth / 2 : chartHorizontalPadding + index * chartInnerWidth / (props.points.length - 1);
}
function hitAreaX(index: number): number {
  const pointWidth: number = props.points.length <= 1 ? chartInnerWidth : chartInnerWidth / (props.points.length - 1);
  return Math.max(chartHorizontalPadding, xFor(index) - pointWidth / 2);
}
function hitAreaWidth(): number {
  return props.points.length <= 1 ? chartInnerWidth : chartInnerWidth / (props.points.length - 1);
}
function statusBandClass(segment: StatusSegment): string {
  return `latency-chart__status-band latency-chart__status-band--${segment.status}`;
}
function statusBoundaryClass(segment: StatusSegment): string {
  return `latency-chart__status-boundary latency-chart__status-boundary--${segment.status}`;
}
function statusSegmentWidth(segment: StatusSegment): number {
  return hitAreaX(segment.lastIndex) + hitAreaWidth() - hitAreaX(segment.firstIndex);
}
function drawsStatusEndBoundary(segment: StatusSegment): boolean {
  const followingPoint: ProbeLatencyPoint | undefined = props.points[segment.lastIndex + 1];
  return followingPoint === undefined || followingPoint.status === "connected";
}
function yFor(value: number): number {
  return chartVerticalPadding + chartInnerHeight - value / maximumLatency.value * chartInnerHeight;
}
function pathFor(definition: SeriesDefinition): string {
  let path: string = "";
  let previousWasMissing: boolean = true;
  for (let index: number = 0; index < props.points.length; index += 1) {
    const point: ProbeLatencyPoint | undefined = props.points[index];
    const value: number | null = point === undefined ? null : point[definition.key];
    if (value === null) {
      previousWasMissing = true;
      continue;
    }
    path += `${previousWasMissing ? "M" : "L"}${xFor(index).toFixed(2)} ${yFor(value).toFixed(2)} `;
    previousWasMissing = false;
  }
  return path;
}
function hasSeries(definition: SeriesDefinition): boolean {
  return props.points.some((point: ProbeLatencyPoint): boolean => point[definition.key] !== null);
}
function formatLatency(value: number | null): string {
  return value === null ? "Not measured" : `${Math.round(value * 100) / 100} ms`;
}
function tooltipText(point: ProbeLatencyPoint): string {
  if (point.sampleCount === 0) {
    return [
      `From: ${formatDateTime(point.startedAt, props.dateTimeFormat)}`,
      `To: ${formatDateTime(point.endedAt, props.dateTimeFormat)}`,
      "No latency data was recorded.",
    ].join("\n");
  }
  if (point.status === "interrupted") {
    return [
      `From: ${formatDateTime(point.startedAt, props.dateTimeFormat)}`,
      `To: ${formatDateTime(point.endedAt, props.dateTimeFormat)}`,
      "State: Interrupted",
      `Samples: ${point.sampleCount}`,
    ].join("\n");
  }
  return [
    `From: ${formatDateTime(point.startedAt, props.dateTimeFormat)}`,
    `To: ${formatDateTime(point.endedAt, props.dateTimeFormat)}`,
    `Primary HTTP: ${formatLatency(point.primaryHttpLatencyMs)}`,
    `DNS: ${formatLatency(point.dnsLatencyMs)}`,
    `Gateway: ${formatLatency(point.gatewayLatencyMs)}`,
    `Samples: ${point.sampleCount}`,
    `State: ${point.status === "degraded" ? "Degraded" : "Connected"}`,
  ].join("\n");
}
async function showTooltip(point: ProbeLatencyPoint, event: MouseEvent | FocusEvent): Promise<void> {
  const targetElement: HTMLElement = event.currentTarget as HTMLElement;
  const targetBounds: DOMRect = targetElement.getBoundingClientRect();
  const anchorX: number = event instanceof MouseEvent ? event.clientX : targetBounds.left + targetBounds.width / 2;
  const anchorY: number = event instanceof MouseEvent ? event.clientY : targetBounds.top;
  tooltipPanel.value = { visible: true, text: tooltipText(point), left: anchorX, top: anchorY, arrowLeft: anchorX };
  await nextTick();
  const tooltip: HTMLElement | null = tooltipElement.value;
  const tooltipWidth: number = tooltip?.offsetWidth ?? 320;
  const tooltipHeight: number = tooltip?.offsetHeight ?? 150;
  const horizontalBounds: { readonly left: number; readonly right: number } = horizontalTooltipBounds(targetElement, ".latency-chart", tooltipViewportMarginPx);
  const maximumLeft: number = horizontalBounds.right - tooltipWidth;
  const minimumLeft: number = horizontalBounds.left;
  const preferredLeft: number = anchorX - tooltipWidth / 2;
  const left: number = maximumLeft < minimumLeft
    ? clampTooltipPosition(preferredLeft, tooltipViewportMarginPx, window.innerWidth - tooltipWidth - tooltipViewportMarginPx)
    : clampTooltipPosition(preferredLeft, minimumLeft, maximumLeft);
  const preferredTop: number = anchorY - tooltipHeight - tooltipGapPx;
  const top: number = preferredTop < tooltipViewportMarginPx
    ? clampTooltipPosition(anchorY + tooltipGapPx, tooltipViewportMarginPx, window.innerHeight - tooltipHeight - tooltipViewportMarginPx)
    : preferredTop;
  tooltipPanel.value = {
    visible: true,
    text: tooltipText(point),
    left,
    top,
    arrowLeft: clampTooltipPosition(anchorX - left, 10, tooltipWidth - 10),
  };
}
function hideTooltip(): void {
  tooltipPanel.value = { visible: false, text: "", left: 0, top: 0, arrowLeft: 0 };
}
</script>

<template>
  <div v-if="points.length === 0" class="latency-chart__empty">No latency measurements in this period.</div>
  <div v-else class="latency-chart">
    <div class="latency-chart__legend" aria-label="Latency chart legend">
      <span v-for="definition in series.filter(hasSeries)" :key="definition.key">
        <i :style="{ backgroundColor: definition.color }"></i>{{ definition.label }}
      </span>
      <small>Scale: 0–{{ Math.ceil(maximumLatency) }} ms</small>
    </div>
    <svg class="latency-chart__plot" :viewBox="`0 0 ${chartWidth} ${chartHeight}`" preserveAspectRatio="none" role="img" aria-label="Latency over time in milliseconds" @mouseleave="hideTooltip">
      <template v-for="segment in statusSegments" :key="`${segment.status}-${segment.firstIndex}`">
        <rect :class="statusBandClass(segment)" :x="hitAreaX(segment.firstIndex)" :y="chartVerticalPadding" :width="statusSegmentWidth(segment)" :height="chartInnerHeight" />
        <line :class="statusBoundaryClass(segment)" :x1="hitAreaX(segment.firstIndex)" :x2="hitAreaX(segment.firstIndex)" :y1="chartVerticalPadding" :y2="chartVerticalPadding + chartInnerHeight" />
        <line v-if="drawsStatusEndBoundary(segment)" :class="statusBoundaryClass(segment)" :x1="hitAreaX(segment.lastIndex) + hitAreaWidth()" :x2="hitAreaX(segment.lastIndex) + hitAreaWidth()" :y1="chartVerticalPadding" :y2="chartVerticalPadding + chartInnerHeight" />
      </template>
      <line v-for="line in yGridLines" :key="line" class="latency-chart__grid" :x1="chartHorizontalPadding" :x2="chartWidth - chartHorizontalPadding" :y1="chartVerticalPadding + chartInnerHeight * line" :y2="chartVerticalPadding + chartInnerHeight * line" />
      <text v-for="axisLabel in yAxisLabels" :key="axisLabel.value" class="latency-chart__axis-label" :x="chartHorizontalPadding - 10" :y="yFor(axisLabel.value)" text-anchor="end" dominant-baseline="middle">{{ axisLabel.label }}</text>
      <path v-for="definition in series.filter(hasSeries)" :key="definition.key" class="latency-chart__line" :stroke="definition.color" :d="pathFor(definition)" />
      <rect v-for="(point, index) in points" :key="point.startedAt" class="latency-chart__hit-area" :x="hitAreaX(index)" :y="chartVerticalPadding" :width="hitAreaWidth()" :height="chartInnerHeight" tabindex="0" :aria-label="tooltipText(point)" @mouseenter="showTooltip(point, $event)" @mousemove="showTooltip(point, $event)" @focus="showTooltip(point, $event)" @blur="hideTooltip" />
    </svg>
    <div v-if="tooltipPanel.visible" ref="tooltipElement" class="latency-chart__tooltip" role="tooltip" :style="{ left: `${tooltipPanel.left}px`, top: `${tooltipPanel.top}px`, '--tooltip-arrow-left': `${tooltipPanel.arrowLeft}px` }">{{ tooltipPanel.text }}</div>
  </div>
</template>
